import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import PageTitle from "../../../layouts/PageTitle";
import { saveProfileAction, uploadLogoAction } from "../../../../store/actions/AuthActions";
import { getAgentInitials, resolveLogoSrc } from "../../../../utils/agent";
import { getAgentDetail } from "../../../../services/TransferService";

const PROFILE_FIELDS = [
	"name",
	"company_name",
	"mobile",
	"email",
	"address",
	"country",
	"state",
	"postal_code",
	"abn_taxfile",
	"gst",
	"gst_number",
	"vat",
	"vat_number",
];

const CONTACT_FIELDS = [
	{ label: "Name", field: "name", icon: "fa-user" },
	{ label: "Company Name", field: "company_name", icon: "fa-building" },
	{ label: "Mobile No", field: "mobile", icon: "fa-phone" },
	{ label: "Email", field: "email", icon: "fa-envelope", type: "email" },
];

const LOCATION_FIELDS = [
	{ label: "Address", field: "address", icon: "fa-map-marker-alt" },
	{ label: "Country", field: "country", icon: "fa-globe-asia" },
	{ label: "State", field: "state", icon: "fa-map" },
	{ label: "Post Code", field: "postal_code", icon: "fa-mail-bulk" },
];

const BUSINESS_FIELDS = [{ label: "ABN / Tax File Number", field: "abn_taxfile", icon: "fa-file-invoice" }];

function SectionLabel({ children }) {
	return (
		<div className="col-12">
			<div
				className="text-muted fw-bold mb-3"
				style={{ fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase" }}
			>
				{children}
			</div>
		</div>
	);
}

function InfoTile({ icon, label, value, action }) {
	return (
		<div className="col-sm-6 col-lg-4 mb-4">
			<div className="d-flex align-items-start" style={{ gap: "0.85rem" }}>
				<div
					className="d-flex align-items-center justify-content-center rounded-circle bg-light text-primary flex-shrink-0"
					style={{ width: "42px", height: "42px", fontSize: "1rem" }}
				>
					<i className={`fa ${icon}`} />
				</div>
				<div className="min-w-0">
					<div
						className="text-muted mb-1"
						style={{ fontSize: "0.7rem", letterSpacing: "0.05em", textTransform: "uppercase" }}
					>
						{label}
					</div>
					{action || <div className="fw-500 text-break">{value || "—"}</div>}
				</div>
			</div>
		</div>
	);
}

function StatusBadge({ active, activeText, inactiveText = "Not registered" }) {
	return (
		<div className="d-flex align-items-center" style={{ gap: "0.4rem" }}>
			<i className={`fa ${active ? "fa-check-circle text-primary" : "fa-times-circle text-muted"}`} />
			<span className="fw-500">{active ? activeText : inactiveText}</span>
		</div>
	);
}

function buildFormFromAgent(agent) {
	const form = {};
	PROFILE_FIELDS.forEach((field) => {
		form[field] = agent[field] != null ? String(agent[field]) : "";
	});
	if (!form.gst) form.gst = "No";
	if (!form.vat) form.vat = "No";
	return form;
}

const AppProfile = () => {
	const dispatch = useDispatch();
	const agent = useSelector((state) => state.auth.auth) || {};

	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [form, setForm] = useState(() => buildFormFromAgent(agent));
	const [passwordModal, setPasswordModal] = useState(false);
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [resettingPassword, setResettingPassword] = useState(false);
	const [creditDetail, setCreditDetail] = useState(null);

	const fileInputRef = useRef(null);
	const logoSrc = resolveLogoSrc(agent.company_logo);

	useEffect(() => {
		if (!agent.id) return;
		getAgentDetail(agent.id)
			.then(setCreditDetail)
			.catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agent.id]);

	// Live balance from get_agent_detail.php once it resolves; falls back to the
	// snapshot captured at login so the chip doesn't flash empty while it loads.
	const creditInfo = creditDetail || agent;

	function startEditing() {
		setForm(buildFormFromAgent(agent));
		setEditing(true);
	}

	function cancelEditing() {
		setForm(buildFormFromAgent(agent));
		setEditing(false);
	}

	function handleChange(field) {
		return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
	}

	function handleSave(e) {
		e.preventDefault();
		setSaving(true);
		const payload = { agent_id: agent.id, ...form };
		dispatch(
			saveProfileAction(
				payload,
				() => {
					setSaving(false);
					setEditing(false);
				},
				() => setSaving(false)
			)
		);
	}

	function handleLogoClick() {
		if (fileInputRef.current) fileInputRef.current.click();
	}

	function handleLogoChange(e) {
		const file = e.target.files && e.target.files[0];
		e.target.value = "";
		if (!file) return;
		if (!file.type.startsWith("image/")) {
			swal("Oops", "Please choose an image file", "error");
			return;
		}
		if (file.size > 2 * 1024 * 1024) {
			swal("Oops", "Image must be smaller than 2MB", "error");
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			setUploading(true);
			dispatch(
				uploadLogoAction(
					agent.id,
					reader.result,
					() => setUploading(false),
					() => setUploading(false)
				)
			);
		};
		reader.readAsDataURL(file);
	}

	function handlePasswordReset(e) {
		e.preventDefault();
		if (newPassword.length < 6) {
			swal("Oops", "Password must be at least 6 characters", "error");
			return;
		}
		if (newPassword !== confirmPassword) {
			swal("Oops", "Passwords do not match", "error");
			return;
		}
		setResettingPassword(true);
		const payload = { agent_id: agent.id, ...buildFormFromAgent(agent), password: newPassword };
		dispatch(
			saveProfileAction(
				payload,
				() => {
					setResettingPassword(false);
					setPasswordModal(false);
					setNewPassword("");
					setConfirmPassword("");
				},
				() => setResettingPassword(false)
			)
		);
	}

	return (
		<div>
			<PageTitle activeMenu="Profile" motherMenu="App" />

			<div className="row">
				<div className="col-lg-12">
					<div className="card px-3 pt-3 pb-0">
						<div style={{ position: "relative" }}>
							<div
								className="rounded"
								style={{
									backgroundImage: `url(${process.env.PUBLIC_URL}/cover.png)`,
									backgroundSize: "cover",
									backgroundPosition: "center",
									height: "220px",
									width: "100%",
								}}
							></div>
							<div
								style={{
									display: "flex",
									alignItems: "flex-end",
									flexWrap: "wrap",
									gap: "1rem",
									marginTop: "-3rem",
									padding: "0 0.5rem 1rem",
								}}
							>
								<div style={{ position: "relative", width: "100px", height: "100px", flex: "0 0 auto" }}>
									{logoSrc ? (
										<img
											src={logoSrc}
											alt="Profile"
											style={{
												width: "100px",
												height: "100px",
												borderRadius: "50%",
												objectFit: "cover",
												border: "4px solid #fff",
												display: "block",
												background: "#fff",
											}}
										/>
									) : (
										<div
											className="bg-primary text-white"
											style={{
												width: "100px",
												height: "100px",
												borderRadius: "50%",
												border: "4px solid #fff",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: "2.25rem",
												fontWeight: 600,
											}}
										>
											{getAgentInitials(agent)}
										</div>
									)}
									<button
										type="button"
										className="btn btn-primary"
										onClick={handleLogoClick}
										disabled={uploading}
										title="Change photo"
										style={{
											position: "absolute",
											bottom: 0,
											right: 0,
											width: "28px",
											height: "28px",
											borderRadius: "50%",
											border: "2px solid #fff",
											padding: 0,
											lineHeight: "24px",
										}}
									>
										<i className="fa fa-camera" style={{ fontSize: "0.75rem" }} />
									</button>
									<input
										ref={fileInputRef}
										type="file"
										accept="image/*"
										className="d-none"
										onChange={handleLogoChange}
									/>
								</div>
								<div className="pb-2">
									<div className="d-flex align-items-center flex-wrap" style={{ gap: "0.75rem" }}>
										<h4 className="text-primary mb-0">{agent.company_name || "—"}</h4>
										{creditInfo.credit === "Yes" && (
											<span
												className="d-inline-flex align-items-center"
												style={{
													gap: "0.4rem",
													padding: "0.3rem 0.9rem",
													borderRadius: "999px",
													background: "var(--rgba-secondary-1)",
													border: "1px solid var(--rgba-secondary-2)",
													color: "var(--secondary)",
													fontSize: "0.8rem",
													fontWeight: 600,
												}}
											>
												<i className="fa fa-gem" />
												Credit Limit: {creditInfo.currency || "AUD"}{" "}
												{Number(creditInfo.credit_limit || 0).toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</span>
										)}
									</div>
									<p className="mb-0 text-muted mt-1">{agent.email}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-xl-12">
					<div className="card">
						<div className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
							<div>
								<h4 className="text-primary mb-1">Details</h4>
								<p className="text-muted mb-0 fs-13">Your account, contact, and business information</p>
							</div>
							{!editing && (
								<button type="button" className="btn btn-primary btn-sm" onClick={startEditing}>
									<i className="fa fa-pen me-1" />
									Edit
								</button>
							)}
						</div>
						<div className="card-body pt-4">
							{!editing ? (
								<>
									<div className="row">
										<SectionLabel>Contact Information</SectionLabel>
										{CONTACT_FIELDS.map((f) => (
											<InfoTile key={f.field} icon={f.icon} label={f.label} value={agent[f.field]} />
										))}
									</div>

									<div className="row mt-2">
										<SectionLabel>Location</SectionLabel>
										{LOCATION_FIELDS.map((f) => (
											<InfoTile key={f.field} icon={f.icon} label={f.label} value={agent[f.field]} />
										))}
									</div>

									<div className="row mt-2">
										<SectionLabel>Business &amp; Tax</SectionLabel>
										{BUSINESS_FIELDS.map((f) => (
											<InfoTile key={f.field} icon={f.icon} label={f.label} value={agent[f.field]} />
										))}
										<InfoTile
											icon="fa-percent"
											label="GST Register"
											action={
												<StatusBadge
													active={agent.gst === "Yes"}
													activeText={agent.gst_number ? `Yes · ${agent.gst_number}` : "Yes"}
												/>
											}
										/>
										<InfoTile
											icon="fa-percent"
											label="VAT Register"
											action={
												<StatusBadge
													active={agent.vat === "Yes"}
													activeText={agent.vat_number ? `Yes · ${agent.vat_number}` : "Yes"}
												/>
											}
										/>
									</div>

									<div className="row mt-2">
										<SectionLabel>Account</SectionLabel>
										<InfoTile
											icon="fa-user-tie"
											label="Account Manager"
											value={agent.accounts_manager}
										/>
										<InfoTile icon="fa-medal" label="Tier" value={agent.tier_name} />
										<InfoTile
											icon="fa-image"
											label="Company Logo"
											action={
												<button
													type="button"
													className="btn btn-outline-primary btn-sm"
													onClick={handleLogoClick}
													disabled={uploading}
												>
													{uploading ? "Uploading..." : "Change Logo"}
												</button>
											}
										/>
										<InfoTile
											icon="fa-lock"
											label="Password"
											action={
												<button
													type="button"
													className="btn btn-outline-primary btn-sm"
													onClick={() => setPasswordModal(true)}
												>
													Reset Password
												</button>
											}
										/>
									</div>
								</>
							) : (
								<form onSubmit={handleSave}>
									<div className="row">
										<SectionLabel>Contact Information</SectionLabel>
										{CONTACT_FIELDS.map((f) => (
											<div className="form-group mb-3 col-md-6" key={f.field}>
												<label className="form-label">
													<i className={`fa ${f.icon} text-primary me-2`} />
													{f.label}
												</label>
												<input
													type={f.type || "text"}
													className="form-control"
													value={form[f.field]}
													onChange={handleChange(f.field)}
													disabled={f.field === "email"}
												/>
												{f.field === "email" && (
													<small className="text-muted">Email cannot be changed here.</small>
												)}
											</div>
										))}
									</div>

									<div className="row mt-2">
										<SectionLabel>Location</SectionLabel>
										{LOCATION_FIELDS.map((f) => (
											<div className="form-group mb-3 col-md-6" key={f.field}>
												<label className="form-label">
													<i className={`fa ${f.icon} text-primary me-2`} />
													{f.label}
												</label>
												<input
													type={f.type || "text"}
													className="form-control"
													value={form[f.field]}
													onChange={handleChange(f.field)}
												/>
											</div>
										))}
									</div>

									<div className="row mt-2">
										<SectionLabel>Business &amp; Tax</SectionLabel>
										{BUSINESS_FIELDS.map((f) => (
											<div className="form-group mb-3 col-md-6" key={f.field}>
												<label className="form-label">
													<i className={`fa ${f.icon} text-primary me-2`} />
													{f.label}
												</label>
												<input
													type={f.type || "text"}
													className="form-control"
													value={form[f.field]}
													onChange={handleChange(f.field)}
												/>
											</div>
										))}
										<div className="form-group mb-3 col-md-6">
											<label className="form-label">GST Register</label>
											<select className="form-control" value={form.gst} onChange={handleChange("gst")}>
												<option value="No">No</option>
												<option value="Yes">Yes</option>
											</select>
										</div>
										{form.gst === "Yes" && (
											<div className="form-group mb-3 col-md-6">
												<label className="form-label">GST Number</label>
												<input
													type="text"
													className="form-control"
													value={form.gst_number}
													onChange={handleChange("gst_number")}
												/>
											</div>
										)}
										<div className="form-group mb-3 col-md-6">
											<label className="form-label">VAT Register</label>
											<select className="form-control" value={form.vat} onChange={handleChange("vat")}>
												<option value="No">No</option>
												<option value="Yes">Yes</option>
											</select>
										</div>
										{form.vat === "Yes" && (
											<div className="form-group mb-3 col-md-6">
												<label className="form-label">VAT Number</label>
												<input
													type="text"
													className="form-control"
													value={form.vat_number}
													onChange={handleChange("vat_number")}
												/>
											</div>
										)}
									</div>

									<button type="submit" className="btn btn-primary me-2" disabled={saving}>
										{saving ? "Saving..." : "Save Changes"}
									</button>
									<button type="button" className="btn btn-secondary light" onClick={cancelEditing} disabled={saving}>
										Cancel
									</button>
								</form>
							)}
						</div>
					</div>
				</div>
			</div>

			{passwordModal && (
				<div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">Reset Password</h5>
								<button type="button" className="btn-close" onClick={() => setPasswordModal(false)}></button>
							</div>
							<form onSubmit={handlePasswordReset}>
								<div className="modal-body">
									<div className="form-group mb-3">
										<label className="form-label">New Password</label>
										<input
											type="password"
											className="form-control"
											value={newPassword}
											onChange={(e) => setNewPassword(e.target.value)}
											required
										/>
									</div>
									<div className="form-group mb-3">
										<label className="form-label">Confirm Password</label>
										<input
											type="password"
											className="form-control"
											value={confirmPassword}
											onChange={(e) => setConfirmPassword(e.target.value)}
											required
										/>
									</div>
								</div>
								<div className="modal-footer">
									<button
										type="button"
										className="btn btn-secondary light"
										onClick={() => setPasswordModal(false)}
										disabled={resettingPassword}
									>
										Cancel
									</button>
									<button type="submit" className="btn btn-primary" disabled={resettingPassword}>
										{resettingPassword ? "Saving..." : "Save Password"}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AppProfile;
